import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { useState } from "react";
import { useIntl } from "react-intl";
import SelectTimezone from "~components/SelectTimezone/SelectTimezone.tsx";
import { BroadcastTypes } from "~enums/broadcastTypes.ts";
import { useAddScrollToBody } from "~hooks/useAddScrollToBody.ts";
import type { BroadcastTableRow } from "~pages/Fixtures/BroadcastPage/types/broadcastTableRow.ts";
import type { LabelValue } from "~types/labelValue.ts";
import { ActionColumn } from "../components/Columns/ActionColumn.tsx";
import DatePickerColumn from "../components/Columns/DatePickerColumn.tsx";
import InputColumn from "../components/Columns/InputColumn.tsx";
import SelectColumn from "../components/Columns/SelectColumn.tsx";

export const useGetBroadcastColumns = (
  handleDelete: (broadcast: BroadcastTableRow) => Promise<void>,
  handleSave: () => Promise<void>,
  handleDiscard: () => void,
  loading: boolean,
) => {
  const { formatMessage } = useIntl();

  const [editableRowId, setEditableRowId] = useState<number | null>(null);
  const { addScroll, restoreScroll } = useAddScrollToBody();

  const closeRowEdit = () => {
    setEditableRowId(null);
    restoreScroll();
  };

  const onSave = async () => {
    await handleSave();
    closeRowEdit();
  };

  const onDiscard = () => {
    handleDiscard();
    closeRowEdit();
  };

  const onDelete = async (broadcast: BroadcastTableRow) => {
    await handleDelete(broadcast);
    closeRowEdit();
  };

  const isEditMode = (row: BroadcastTableRow) => editableRowId === row.id;

  const columns: ColumnType<BroadcastTableRow>[] = [
    {
      title: formatMessage({ id: "fixture.broadcast.broadcaster" }),
      dataIndex: "broadcaster",
      width: 50,
      render: (value: string, row: BroadcastTableRow) =>
        isEditMode(row) ? (
          <InputColumn
            value={value}
            onChange={(value) => {
              row.broadcaster = value?.toString();
            }}
          />
        ) : (
          value
        ),
    },
    {
      title: formatMessage({ id: "fixture.broadcast.broadcastType" }),
      dataIndex: "broadcastType",
      width: 120,
      render: (value: string, row: BroadcastTableRow) =>
        isEditMode(row) ? (
          <SelectColumn
            value={value}
            allowClear
            options={Object.values(BroadcastTypes).map((type) => ({
              value: type,
              label: type,
            }))}
            onSelect={(value) => {
              row.broadcastType = value;
            }}
          />
        ) : (
          value
        ),
    },
    {
      title: formatMessage({ id: "fixture.broadcast.broadcastURL" }),
      dataIndex: "broadcastURL",
      width: 210,
      render: (value: string, row: BroadcastTableRow) =>
        isEditMode(row) ? (
          <InputColumn
            value={value}
            onChange={(value) => {
              row.broadcastURL = value?.toString();
            }}
          />
        ) : (
          value
        ),
    },
    {
      title: formatMessage({ id: "fixture.broadcast.broadcasterURL" }),
      dataIndex: "broadcasterURL",
      width: 210,
      render: (value: string, row: BroadcastTableRow) =>
        isEditMode(row) ? (
          <InputColumn
            value={value}
            onChange={(value) => {
              row.broadcasterURL = value?.toString();
            }}
          />
        ) : (
          value
        ),
    },
    {
      title: formatMessage({ id: "fixture.broadcast.broadcastTimezone" }),
      dataIndex: "broadcastTimezone",
      width: 120,
      render: (value: string, row: BroadcastTableRow) =>
        isEditMode(row) ? (
          <SelectTimezone
            value={value}
            onChange={(option) => {
              row.broadcastTimezone = (option as LabelValue)?.value ?? "";
            }}
          />
        ) : (
          value
        ),
    },
    {
      title: formatMessage({ id: "fixture.broadcast.locale" }),
      dataIndex: "locale",
      width: 100,
      render: (value: string, row: BroadcastTableRow) =>
        isEditMode(row) ? (
          <InputColumn
            value={value}
            onChange={(value) => {
              row.locale = value?.toString();
            }}
          />
        ) : (
          value
        ),
    },
    {
      title: formatMessage({ id: "fixture.broadcast.startTimeLocal" }),
      dataIndex: "startTimeLocal",
      width: 135,
      render: (value: string, row: BroadcastTableRow) =>
        isEditMode(row) ? (
          <DatePickerColumn
            showTime
            value={value}
            onChange={(value) => {
              row.startTimeLocal = value?.toString();
            }}
          />
        ) : (
          value && dayjs(value).format("DD/MM/YYYY - HH:mm:ss")
        ),
    },
    {
      render: (_: unknown, row: BroadcastTableRow) => (
        <ActionColumn
          loading={loading}
          editableRow={editableRowId}
          row={row}
          onSave={onSave}
          onDiscard={onDiscard}
          handleDelete={onDelete}
          setEditableRow={(rowId) => {
            setEditableRowId(rowId);
            addScroll();
          }}
        />
      ),
      fixed: "right",
      width: 100,
    },
  ];

  return columns;
};
